import { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

/**
 * Raw Three.js scene (no JSX/R3F) to avoid visual-edits JSX instrumentation
 * that conflicts with React Three Fiber's prop setter.
 */
export default function HeroScene() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let width = container.clientWidth;
    let height = container.clientHeight;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 5);

    // Environment for reflections
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.3));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(5, 5, 5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x9999aa, 0.6);
    rim.position.set(-5, -3, -5);
    scene.add(rim);

    // Central morphing icosahedron
    const geo = new THREE.IcosahedronGeometry(1.6, 32);
    const mat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0.85,
      roughness: 0.15,
      clearcoat: 0.6,
      clearcoatRoughness: 0.2,
      envMapIntensity: 1.4,
    });
    const sphere = new THREE.Mesh(geo, mat);
    scene.add(sphere);

    // Store original positions for distortion
    const positionAttr = geo.attributes.position;
    const original = positionAttr.array.slice();

    // Rings
    const rings = [];
    const ringConfigs = [
      { radius: 2.4, tube: 0.012, rot: [Math.PI / 2.5, 0, 0], speed: 0.003 },
      { radius: 2.7, tube: 0.012, rot: [Math.PI / 1.8, Math.PI / 4, 0], speed: -0.0025 },
      { radius: 3.0, tube: 0.008, rot: [Math.PI / 3, Math.PI / 2, 0], speed: 0.0015 },
    ];
    ringConfigs.forEach((cfg) => {
      const rg = new THREE.TorusGeometry(cfg.radius, cfg.tube, 16, 200);
      const rm = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffffff,
        emissiveIntensity: 0.15,
        roughness: 0.4,
      });
      const ring = new THREE.Mesh(rg, rm);
      ring.rotation.set(cfg.rot[0], cfg.rot[1], cfg.rot[2]);
      ring.userData.speed = cfg.speed;
      scene.add(ring);
      rings.push(ring);
    });

    // Animation
    const clock = new THREE.Clock();
    let frameId;
    const animate = () => {
      const t = clock.getElapsedTime();

      // Distort sphere vertices
      for (let i = 0; i < positionAttr.count; i++) {
        const ox = original[i * 3];
        const oy = original[i * 3 + 1];
        const oz = original[i * 3 + 2];
        const r = Math.sqrt(ox * ox + oy * oy + oz * oz);
        const noise =
          0.18 *
          Math.sin(ox * 2 + t * 0.8) *
          Math.cos(oy * 2 + t * 0.6) *
          Math.sin(oz * 2 + t * 0.7);
        const factor = 1 + noise / r;
        positionAttr.array[i * 3] = ox * factor;
        positionAttr.array[i * 3 + 1] = oy * factor;
        positionAttr.array[i * 3 + 2] = oz * factor;
      }
      positionAttr.needsUpdate = true;
      geo.computeVertexNormals();

      sphere.rotation.y = t * 0.15;
      sphere.rotation.x = Math.sin(t * 0.2) * 0.2;
      sphere.position.y = Math.sin(t * 0.6) * 0.08;

      rings.forEach((r) => {
        r.rotation.x += r.userData.speed;
        r.rotation.y += r.userData.speed * 1.1;
      });

      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    // Resize
    const onResize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      pmrem.dispose();
      geo.dispose();
      mat.dispose();
      rings.forEach((r) => {
        r.geometry.dispose();
        r.material.dispose();
      });
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="hero-3d-canvas"
      className="absolute inset-0"
      style={{ width: "100%", height: "100%" }}
    />
  );
}
